import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type { PublicRoom, TimetableItem } from "../../domain/contracts/public-booking";
import type { PublicStudentProfileDTO } from "../../domain/contracts/public-student-profile";

export type PublicCreateRoomBookingInput = {
  roomId: string;
  date: string;
  startTime: string;
  endTime: string;
  contactName: string;
  contactPhone: string;
  title?: string;
  /** ກີບ — 0 = ບໍ່ຄິດເງິນ */
  totalAmount?: number;
  amountPaidNow?: number;
  pricingNotes?: string;
};

export type PublicCreateRoomBookingResponse = {
  id: string;
  message?: string;
  credit?: {
    invoiceId: string;
    totalAmount: number;
    paidAmount: number;
    remainingAmount: number;
    status: string;
  };
};

export type PublicOutstandingRoomBookingItem = {
  bookingId: string;
  invoiceId: string;
  date: string;
  startTime: string;
  endTime: string;
  roomName: string | null;
  contactName: string | null;
  contactPhone: string | null;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  status: string;
};

export type PublicPayRoomBookingResponse = {
  message: string;
  invoiceId: string;
  paidAmount: number;
  remainingAmount: number;
  status: string;
};

export const publicBookingApi = {
  async getTimetable(params: {
    date?: string;
    start?: string;
    end?: string;
  }): Promise<{ data: TimetableItem[] }> {
    const url = new URL(`${config.apiUrl}/public/timetable`);
    if (params.date) url.searchParams.set("date", params.date);
    if (params.start) url.searchParams.set("start", params.start);
    if (params.end) url.searchParams.set("end", params.end);
    return fetcher.get<{ data: TimetableItem[] }>(url.toString());
  },

  async getRooms(): Promise<{ data: PublicRoom[] }> {
    return fetcher.get<{ data: PublicRoom[] }>(`${config.apiUrl}/public/rooms`);
  },

  async createRoomBooking(
    input: PublicCreateRoomBookingInput,
  ): Promise<PublicCreateRoomBookingResponse> {
    return fetcher.post<PublicCreateRoomBookingResponse>(
      `${config.apiUrl}/public/room-bookings`,
      {
        totalAmount: 0,
        amountPaidNow: 0,
        ...input,
      },
    );
  },

  async lookupOutstandingRoomBookings(input: {
    contactPhone: string;
    date?: string;
    bookingId?: string;
  }): Promise<{ items: PublicOutstandingRoomBookingItem[] }> {
    return fetcher.post<{ items: PublicOutstandingRoomBookingItem[] }>(
      `${config.apiUrl}/public/room-bookings/lookup-outstanding`,
      input,
    );
  },

  async payRoomBookingBalance(
    bookingId: string,
    input: { contactPhone: string; amount: number; notes?: string },
  ): Promise<PublicPayRoomBookingResponse> {
    return fetcher.post<PublicPayRoomBookingResponse>(
      `${config.apiUrl}/public/room-bookings/${encodeURIComponent(bookingId)}/payments`,
      input,
    );
  },

  async getPublicStudentProfile(
    memberId: string,
  ): Promise<PublicStudentProfileDTO> {
    return fetcher.get<PublicStudentProfileDTO>(
      `${config.apiUrl}/public/student-profile/${encodeURIComponent(memberId)}`,
    );
  },
};
